/**
 * ReportInputGate — the between-jobs card.
 *
 * Mounted on the field HOME (the between-jobs moment), never on a job screen,
 * so it can't interrupt work on site. Asks the server whether this tester has
 * an overdue flagged report and in which mode:
 *   soft  → a dismissible nudge (the default rollout: tab + push, no wall)
 *   hard  → a full-screen wall (flipped on via test_input.hard_gate_enabled)
 * Even the wall keeps the escape hatches (do-it-now / city hold / — unless
 * forced — "can't right now"). Fails open: any error means no gate.
 *
 * #1100 — the card is handed `redesign` here too. #950 wired the flag into the
 * Test Queue tab and stopped there, so with `field_redesign_v2` ON this nudge
 * still rendered the OLD card: the zip link and the truncated filenames, on the
 * surface a tech meets most often. Same component, same flag, two call sites —
 * and only one of them was passed the prop. `useFieldRedesign` fails closed, so
 * an error or a loading state here is still today's card; the gate's own
 * fail-OPEN behaviour (an error means no gate at all) is unchanged.
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getGate } from "@/lib/report-input";
import { ReportInputCard } from "@/components/report-input-card";
import { useFieldRedesign } from "@/lib/use-field-redesign";

export function ReportInputGate() {
    const qc = useQueryClient();
    const { enabled: redesign } = useFieldRedesign();
    const [dismissed, setDismissed] = useState(false);
    const gate = useQuery({
        queryKey: ["report-input", "gate"],
        queryFn: getGate,
        staleTime: 0,
        refetchOnWindowFocus: true,
    });

    if (dismissed) return null;
    const data = gate.data;
    if (!data?.show || !data.packet) return null;

    const isWall = data.mode === "hard";
    const resolved = () => {
        void qc.invalidateQueries({ queryKey: ["report-input"] });
        setDismissed(true);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-3 sm:items-center">
            {/* 17 photos run the card past an iPad screen; cap it and let it scroll so
                the file-it steps and Mark submitted stay reachable. */}
            <div className="max-h-[calc(100dvh-1.5rem)] w-full max-w-md overflow-y-auto overscroll-contain rounded-2xl bg-background p-5 shadow-2xl">
                <div className="mb-3 text-center">
                    <div className="text-[11px] font-bold uppercase tracking-wide text-primary">
                        {isWall ? "Office needs this one in" : "Before your next job"}
                    </div>
                    <div className="mt-1 text-lg font-extrabold">Quick one — let&rsquo;s knock it out 👊</div>
                    <p className="mt-1 text-sm text-muted-foreground">
                        This report&rsquo;s been waiting. About two minutes and everything you need is right here.
                    </p>
                </div>
                <ReportInputCard
                    packet={data.packet}
                    variant={isWall ? "wall" : "nudge"}
                    onResolved={resolved}
                    onDismiss={isWall ? undefined : () => setDismissed(true)}
                    redesign={redesign}
                />
            </div>
        </div>
    );
}
